import { useState } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { useAdminApi } from '@/hooks/useAdminApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Settings, Users } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminSettings() {
  const { updateAllUsersLimits, isLoading } = useAdminApi();
  const [bulkStorageLimit, setBulkStorageLimit] = useState(100);
  const [bulkAiLimit, setBulkAiLimit] = useState(100);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleUpdateAllLimits = async () => {
    setIsSubmitting(true);
    try {
      await updateAllUsersLimits(bulkStorageLimit, bulkAiLimit);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update limits';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Settings</h1>
          <p className="text-muted-foreground">Configure platform-wide settings</p>
        </div>

        {/* Bulk User Limits */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Update All User Limits
            </CardTitle>
            <CardDescription>
              Set storage and AI limits for all existing users at once
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="bulkStorage">Storage Limit (MB)</Label>
                <Input
                  id="bulkStorage"
                  type="number"
                  value={bulkStorageLimit}
                  onChange={(e) => setBulkStorageLimit(parseInt(e.target.value) || 100)}
                  min={1}
                />
                <p className="text-xs text-muted-foreground">
                  Applied to all existing users
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="bulkAi">AI Requests Limit</Label>
                <Input
                  id="bulkAi"
                  type="number"
                  value={bulkAiLimit}
                  onChange={(e) => setBulkAiLimit(parseInt(e.target.value) || 100)}
                  min={1}
                />
                <p className="text-xs text-muted-foreground">
                  Applied to all existing users
                </p>
              </div>
            </div>
            <Button 
              onClick={handleUpdateAllLimits} 
              disabled={isSubmitting || isLoading}
            >
              {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Update All Users
            </Button>
          </CardContent>
        </Card>

        {/* Admin Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Admin Configuration
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-2 border-b">
                <span className="text-muted-foreground">Admin Emails</span>
                <span className="font-mono">studio@faajistudios.com</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-muted-foreground">Default Storage Limit</span>
                <span>100 MB</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-muted-foreground">Default AI Limit</span>
                <span>100 requests</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
